import React from 'react';
import Icon from '../../../components/AppIcon';

const TechnicalRequirements = () => {
  const requirements = {
    minimum: [
      { label: "Operating System", value: "Windows 10 (64-bit) or later" },
      { label: "Processor", value: "Intel Core i3 or AMD equivalent" },
      { label: "RAM", value: "4 GB" },
      { label: "Storage", value: "500 MB available space" },
      { label: "Display", value: "1366 x 768 resolution" },
      { label: "Additional", value: "USB port for barcode scanner/printer" }
    ],
    recommended: [
      { label: "Operating System", value: "Windows 11 (64-bit)" },
      { label: "Processor", value: "Intel Core i5 or AMD Ryzen 5" },
      { label: "RAM", value: "8 GB or more" },
      { label: "Storage", value: "1 GB SSD available space" },
      { label: "Display", value: "1920 x 1080 resolution or higher" },
      { label: "Additional", value: "Multiple USB ports, Ethernet connection" }
    ]
  };

  // note: Desktop app is built with Electron and stores local data in SQLite
  const stackNote = 'Built with Electron + React. Local storage uses SQLite for fast, reliable local-first operations. License data and backups are encrypted (AES-256).';

  const compatibleDevices = [
    {
      id: 1,
      name: "Thermal Receipt Printers",
      icon: "Printer",
      examples: "Epson TM-T20, Star TSP143, Bixolon SRP-350"
    },
    {
      id: 2,
      name: "Barcode Scanners",
      icon: "Scan",
      examples: "Honeywell Voyager, Zebra DS2208, Symbol LS2208"
    },
    {
      id: 3,
      name: "Cash Drawers",
      icon: "DollarSign",
      examples: "APG Vasario, Star SMD2, M-S Cash Drawer"
    },
    {
      id: 4,
      name: "Customer Displays",
      icon: "Monitor",
      examples: "Bematech LD220, Logic Controls PD3000, Partner Tech"
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
            System Requirements
          </h2>
          <p className="font-body text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            Ensure your hardware meets these specifications for optimal performance. Most modern computers exceed these requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto mb-12 md:mb-16">
          <div className="bg-card rounded-xl border border-border shadow-purple-md p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg">
                <Icon name="AlertCircle" size={24} color="var(--color-warning)" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                Minimum Requirements
              </h3>
            </div>

            <div className="space-y-4">
              {requirements?.minimum?.map((req, index) => (
                <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-border last:border-0">
                  <span className="font-body text-sm md:text-base font-medium text-muted-foreground">
                    {req?.label}
                  </span>
                  <span className="font-body text-sm md:text-base text-foreground">
                    {req?.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-xl border border-primary shadow-purple-lg p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg">
                <Icon name="CheckCircle" size={24} color="var(--color-success)" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                Recommended Specifications
              </h3>
            </div>

            <div className="space-y-4">
              {requirements?.recommended?.map((req, index) => (
                <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-border last:border-0">
                  <span className="font-body text-sm md:text-base font-medium text-muted-foreground">
                    {req?.label}
                  </span>
                  <span className="font-body text-sm md:text-base text-foreground">
                    {req?.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3 md:mb-4">
              Compatible Hardware Devices
            </h3>
            <p className="font-body text-sm md:text-base text-muted-foreground">
              Cultaz Desktop POS works seamlessly with industry-standard retail hardware
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {compatibleDevices?.map((device) => (
              <div
                key={device?.id}
                className="bg-card rounded-xl border border-border shadow-purple-sm transition-smooth hover:shadow-purple-md p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <Icon name={device?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-2">
                      {device?.name}
                    </h4>
                    <p className="font-body text-sm md:text-base text-muted-foreground">
                      {device?.examples}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 md:mt-10 p-6 md:p-8 bg-primary/5 rounded-xl border border-primary/20">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <Icon name="Info" size={24} color="var(--color-primary)" strokeWidth={2} className="flex-shrink-0" />
              <div>
                <h4 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-2">
                  Hardware Compatibility Note
                </h4>
                <p className="font-body text-sm md:text-base text-muted-foreground">
                  Most USB and Bluetooth-enabled retail hardware devices are compatible with Cultaz Desktop POS. Contact our support team for specific device compatibility verification before purchase.
                </p>
                <p className="font-body text-sm md:text-base text-muted-foreground mt-3">
                  {stackNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalRequirements;